import * as XLSX from "xlsx";

const normalizeText = (value) => String(value ?? "").trim();

const normalizeHeader = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const normalizeAccountName = (value) =>
  normalizeText(value).toLowerCase().replace(/\s+/g, " ");

function pad2(value) {
  return String(value).padStart(2, "0");
}

function isValidISODate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Always returns the same format used by the manual <input type="date">:
 * YYYY-MM-DD
 */
function normalizeTransactionDate(value) {
  if (value === null || value === undefined || value === "") return "";

  // Real Excel date serial.
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return "";

    const iso = `${parsed.y}-${pad2(parsed.m)}-${pad2(parsed.d)}`;
    return isValidISODate(iso) ? iso : "";
  }

  // When XLSX.read(..., cellDates:true) gives a Date object.
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const iso = `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
      value.getDate()
    )}`;
    return isValidISODate(iso) ? iso : "";
  }

  const text = normalizeText(value);

  // Preferred text format: YYYY-MM-DD. If a time follows it, keep the date part.
  const isoPrefix = text.match(/^(\d{4}-\d{2}-\d{2})(?:[ T].*)?$/);
  if (isoPrefix) {
    return isValidISODate(isoPrefix[1]) ? isoPrefix[1] : "";
  }

  // Excel/user-entered dates are often displayed as YYYY/MM/DD even when
  // the template column is formatted as yyyy-mm-dd. Accept that safely and
  // normalize it to the exact same YYYY-MM-DD string used by manual entry.
  const ymdSlash = text.match(
    /^(\d{4})[\/.](\d{1,2})[\/.](\d{1,2})(?:[ T].*)?$/
  );
  if (ymdSlash) {
    const iso = `${ymdSlash[1]}-${pad2(ymdSlash[2])}-${pad2(ymdSlash[3])}`;
    return isValidISODate(iso) ? iso : "";
  }

  // Also accept DD/MM/YYYY or DD-MM-YYYY, with an optional time,
  // and normalize to YYYY-MM-DD.
  const dmy = text.match(
    /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:\s+.*)?$/
  );
  if (dmy) {
    const iso = `${dmy[3]}-${pad2(dmy[2])}-${pad2(dmy[1])}`;
    return isValidISODate(iso) ? iso : "";
  }

  return "";
}

function parseAmount(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;

  const cleaned = normalizeText(value)
    .replace(/AED/gi, "")
    .replace(/,/g, "")
    .trim();

  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function findColumn(row, aliases) {
  const normalizedAliases = new Set(aliases.map(normalizeHeader));

  for (const [key, value] of Object.entries(row || {})) {
    if (normalizedAliases.has(normalizeHeader(key))) return value;
  }

  return "";
}

function isBlankImportRow(row) {
  const values = Object.values(row || {});
  return values.every((value) => normalizeText(value) === "");
}

/**
 * Excel import is intentionally PREVIEW/ENTRY ONLY.
 * This function never writes to Firestore and never changes account balances.
 */
export async function parseTransactionExcel(file, accounts) {
  if (!file) throw new Error("Please select an Excel file.");

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: true,
  });

  if (!workbook.SheetNames.length) {
    throw new Error("The Excel file does not contain a worksheet.");
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const excelRows = XLSX.utils.sheet_to_json(sheet, {
    raw: true,
    defval: "",
  });

  const accountByName = new Map(
    (accounts || [])
      .filter((account) => account?.id && account?.name)
      .map((account) => [normalizeAccountName(account.name), account])
  );

  const importedRows = [];
  const errors = [];

  excelRows.forEach((excelRow, index) => {
    if (isBlankImportRow(excelRow)) return;

    const excelRowNo = index + 2; // header is row 1

    const accountName = normalizeText(
      findColumn(excelRow, ["Account Head", "Account", "Account Name"])
    );
    const rawDate = findColumn(excelRow, ["Date", "Transaction Date"]);
    const expenseType = normalizeText(
      findColumn(excelRow, ["Type", "Expense Type", "Transaction Type"])
    );
    const description = normalizeText(
      findColumn(excelRow, ["Description", "Details", "Narration"])
    );
    const amountIn = parseAmount(
      findColumn(excelRow, ["Amount In", "In", "Debit In"])
    );
    const amountOut = parseAmount(
      findColumn(excelRow, ["Amount Out", "Out", "Credit Out"])
    );
    const paidBy = normalizeText(
      findColumn(excelRow, ["Paid By", "Payment By", "PaidBy"])
    );
    const comment = normalizeText(
      findColumn(excelRow, ["Comment", "Comments", "Remarks", "Remark"])
    );

    const account = accountByName.get(normalizeAccountName(accountName));
    const date = normalizeTransactionDate(rawDate);

    if (!accountName) {
      errors.push(`Excel row ${excelRowNo}: Account Head is required.`);
      return;
    }

    if (!account) {
      errors.push(
        `Excel row ${excelRowNo}: Account Head "${accountName}" does not exactly match an existing account.`
      );
      return;
    }

    if (!date) {
      errors.push(
        `Excel row ${excelRowNo}: Invalid Date "${normalizeText(rawDate)}". Use an Excel date, YYYY-MM-DD, YYYY/MM/DD, DD/MM/YYYY, or DD-MM-YYYY.`
      );
      return;
    }

    if (!expenseType && !description) {
      errors.push(
        `Excel row ${excelRowNo}: Either Type or Description is required.`
      );
      return;
    }

    if (Number.isNaN(amountIn) || Number.isNaN(amountOut)) {
      errors.push(`Excel row ${excelRowNo}: Invalid Amount In/Amount Out.`);
      return;
    }

    if (amountIn < 0 || amountOut < 0) {
      errors.push(`Excel row ${excelRowNo}: Amounts cannot be negative.`);
      return;
    }

    if (amountIn === 0 && amountOut === 0) {
      errors.push(
        `Excel row ${excelRowNo}: Either Amount In or Amount Out must be greater than 0.`
      );
      return;
    }

    importedRows.push({
      accountId: account.id,
      date,
      expenseType,
      description,
      amountIn: amountIn ? String(amountIn) : "",
      amountOut: amountOut ? String(amountOut) : "",
      paidBy,
      comment,
      proofFile: null,
      error: null,
    });
  });

  if (!excelRows.length) {
    errors.push("The Excel worksheet contains no transaction rows.");
  }

  const totalIn = importedRows.reduce(
    (sum, row) => sum + Number(row.amountIn || 0),
    0
  );
  const totalOut = importedRows.reduce(
    (sum, row) => sum + Number(row.amountOut || 0),
    0
  );

  return {
    rows: importedRows,
    errors,
    totalIn,
    totalOut,
    sourceRowCount: excelRows.filter((row) => !isBlankImportRow(row)).length,
  };
}
